import { useEffect, useRef, useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import apiClient from '@services/api/client'

/**
 * Affiche le lien de paiement Wave sous forme de QR code à scanner avec le
 * téléphone (l'app Wave n'est pas installable sur ordinateur).
 * Détecte automatiquement la validation du paiement (poll sur etatPaiement).
 */
export default function WaveQRModal({ waveUrl, postId, amount, onClose }) {
  const navigate = useNavigate()
  const [copied, setCopied] = useState(false)
  const [paid, setPaid] = useState(false)
  const pollRef = useRef(null)

  // Poll : détecte quand le paiement est validé côté serveur (etatPaiement = 1)
  useEffect(() => {
    if (!postId) return
    pollRef.current = setInterval(async () => {
      try {
        const res = await apiClient.get(`/posts/${postId}`)
        const post = res?.data || res
        if (Number(post?.etatPaiement) === 1) {
          clearInterval(pollRef.current)
          setPaid(true)
          toast.success('Paiement confirmé !')
          setTimeout(() => navigate('/dashboard', { state: { paymentSuccess: true } }), 1800)
        }
      } catch { /* on réessaie au prochain tick */ }
    }, 3000)
    return () => clearInterval(pollRef.current)
  }, [postId, navigate])

  const copyLink = () => {
    navigator.clipboard.writeText(waveUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-[#001F3F]">Payer avec Wave</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {paid ? (
          /* État payé */
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-lg font-bold text-[#001F3F]">Paiement confirmé !</p>
            <p className="text-sm text-gray-500 mt-1">Redirection en cours…</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-600 text-center mb-4">
              Scannez ce QR code avec l'application <span className="font-semibold">Wave</span> sur votre téléphone pour payer
              {amount ? <> <span className="font-semibold">{Number(amount).toLocaleString('fr-FR')} FCFA</span></> : null}.
            </p>

            {/* QR code */}
            <div className="flex justify-center mb-4">
              <div className="p-4 bg-white border-2 border-gray-100 rounded-xl">
                <QRCodeSVG value={waveUrl} size={220} level="M" includeMargin />
              </div>
            </div>

            {/* Lien (copie / ouverture directe) */}
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={waveUrl}
                readOnly
                className="flex-1 px-3 py-2 bg-gray-100 rounded-lg text-xs text-gray-600 truncate"
              />
              <button
                onClick={copyLink}
                className="px-3 py-2 bg-[#D4AF37] text-white rounded-lg hover:bg-[#c09d2a] transition-colors text-sm font-medium whitespace-nowrap"
              >
                {copied ? 'Copié !' : 'Copier'}
              </button>
            </div>

            <a
              href={waveUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full text-center px-4 py-2.5 border border-[#D4AF37] text-[#D4AF37] rounded-lg hover:bg-[#D4AF37]/10 transition-colors text-sm font-medium mb-4"
            >
              Ouvrir le lien de paiement
            </a>

            {/* Statut d'attente */}
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
              <span className="w-4 h-4 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
              En attente du paiement…
            </div>
          </>
        )}
      </div>
    </div>
  )
}
